import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard, Users, Box, Truck, UserCog, Network, Layers,
  Sparkles, Workflow, Wrench, ScrollText, Boxes, LogOut, ShieldCheck,
  Award, Handshake, Building2, Clock, Gift, MapPin, Megaphone, Target,
} from "lucide-react";
import { Toaster } from "sonner";
import Brand from "../Brand";
import CrmAiAssistant from "./CrmAiAssistant";
import NotificationBell from "../NotificationBell";

const NAV = [
  { to: "/crm", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/crm/leads", label: "Leads", icon: Sparkles },
  { to: "/crm/opportunities", label: "Opportunities", icon: Workflow },
  { to: "/crm/sales-targets", label: "Sales Target", icon: Target },
  { to: "/crm/quotations", label: "Quotations", icon: ScrollText },
  { to: "/crm/tickets", label: "Service Tickets", icon: Wrench },
  { to: "/crm/installed-base", label: "Installed Base", icon: ShieldCheck },
  { to: "/crm/customers", label: "Customers", icon: Users },
  { to: "/crm/materials", label: "Materials", icon: Box },
  { to: "/crm/inventory", label: "Inventory", icon: Boxes },
  { to: "/crm/vendors", label: "Vendors", icon: Truck },
  { to: "/crm/employees", label: "Sales Employees", icon: UserCog },
  { to: "/crm/technicians", label: "Technicians", icon: UserCog },
  { to: "/crm/service-team", label: "Service Team", icon: Building2 },
  { to: "/crm/sla", label: "SLA Master", icon: Clock },
  { to: "/crm/solar-consultants", label: "Solar Consultants", icon: Handshake },
  { to: "/crm/referrer-payouts", label: "Referrer Payouts", icon: Gift },
  { to: "/crm/incentive-slabs", label: "Incentive Slabs", icon: Award },
  { to: "/crm/permissible-territories", label: "Permissible Territories", icon: MapPin },
  { to: "/crm/customer-groups", label: "Customer Groups", icon: Network },
  { to: "/crm/material-groups", label: "Material Groups", icon: Layers },
  { to: "/crm/campaigns", label: "Campaign Creatives", icon: Megaphone },
];

export default function CrmLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  async function handleLogout() {
    await logout();
    nav("/login");
  }

  return (
    <div className="min-h-screen bg-[#FFFCF7] text-[#0F2A14]">
      <div className="grid grid-cols-[260px_1fr] min-h-screen">
        {/* Sidebar */}
        <aside className="border-r border-[#E8E2D6] bg-white flex flex-col">
          <div className="px-6 py-5 border-b border-[#E8E2D6]">
            <Brand variant="full" to="/crm" size={36} />
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4A5568] mt-2 ml-[46px]">
              Backend Console
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto py-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  "flex items-center gap-3 px-6 py-2.5 text-sm border-l-2 transition-colors " +
                  (isActive
                    ? "border-[#1B5E20] bg-[#E8F5E9] text-[#0F2A14] font-medium"
                    : "border-transparent text-[#4A5568] hover:bg-[#FAF6EE] hover:text-[#0F2A14]")
                }
              >
                <n.icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-[#E8E2D6] p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-xs font-mono text-[#4A5568] uppercase tracking-widest">{user?.role}</div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="p-2 text-[#4A5568] hover:text-[#1B5E20]"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 text-[13px]">
          <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-[#E8E2D6] px-6 py-2.5 flex items-center justify-between">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#4A5568]">
              Backend Workbench / Single Source of Truth
            </div>
            <div className="flex items-center gap-3 text-[12px]">
              <Link to="/app" className="text-[#4A5568] hover:text-[#1B5E20]">Customer App ↗</Link>
              <Link to="/fsm" className="text-[#4A5568] hover:text-[#1B5E20]">Technician FSM ↗</Link>
              <NotificationBell iconClassName="w-3.5 h-3.5" />
            </div>
          </header>
          <div className="p-6 max-w-[1600px]">
            <Outlet />
          </div>
        </main>
      </div>
      <CrmAiAssistant />
      <Toaster position="top-right" richColors />
    </div>
  );
}
